/**
 * One Engagement Amendment may cover several engagements of the same company
 * (same client group + FYE year). Each engagement is checked against the
 * engagements already in the database, but two tabs of the SAME request can
 * also land in the same slot (company + FYE + service) — e.g. both changed to
 * Agreed Upon Procedures. This is that check, shared by the New page / Modify
 * modal (warning, Submit refused) and the server (POST / PATCH 400, approval
 * 409 CANNOT_APPLY), so the request never gets approved half-applied.
 *
 * Tabs apply in request order, so in a slot two tabs share:
 *   - a service NOT flagged "Multiple per FYE" → never allowed;
 *   - a flagged service → every tab after the first that moved into the slot
 *     needs a Description of Service, and no two tabs may carry the same one
 *     (the same rule services/engagementSlot applies against the database).
 *
 * CommonJS so the server can require it (like fsAuditOpinion.js).
 *
 * @param items  [{ name, entityId, yearEnd, service, description, moved }] in
 *               request order — each tab's values AFTER the amendment; `moved`
 *               = the tab changes its company, FYE or service.
 * @param isMulti (service) => boolean — the "Multiple per FYE" flag.
 * @returns null, or { code, message }.
 */
const norm = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();

const earSlotConflict = (items, isMulti) => {
  const groups = new Map();
  for (const it of items || []) {
    if (!it || it.entityId == null || !it.yearEnd || !norm(it.service)) continue;
    const key = `${it.entityId}|${String(it.yearEnd).slice(0, 10)}|${norm(it.service)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(it);
  }
  for (const group of groups.values()) {
    if (group.length < 2 || !group.some((it) => it.moved)) continue;
    const service = group.find((it) => it.moved).service;
    const [first, second] = group;
    if (!isMulti(service)) {
      return {
        code: 'DUPLICATE_ENGAGEMENT',
        message: `"${first.name}" and "${second.name}" would both become ${service} for the same company and FYE — only one such engagement is allowed. Change one of them.`,
      };
    }
    const seen = [];
    for (let i = 0; i < group.length; i += 1) {
      const it = group[i];
      const d = norm(it.description);
      if (i > 0 && it.moved && !d) {
        return {
          code: 'DESCRIPTION_REQUIRED',
          message: `"${group[0].name}" and "${it.name}" would both become ${service} for the same company and FYE — enter a Description of Service on "${it.name}" to tell them apart.`,
        };
      }
      const twin = d ? seen.find((s) => s.d === d) : null;
      if (twin) {
        return {
          code: 'DUPLICATE_DESCRIPTION',
          message: `"${twin.it.name}" and "${it.name}" would both become ${service} for the same company and FYE with the same Description of Service — give each its own description.`,
        };
      }
      if (d) seen.push({ d, it });
    }
  }
  return null;
};

module.exports = { earSlotConflict };
